<template>
  <v-data-table
    v-if="model"
    :headers="headers"
    v-model:page="page"
    :items="filteredItems"
    :items-per-page="itemsPerPage"
    :loading="loading"
    density="comfortable"
    :items-per-page-options="itemsPerPageOptions"
    show-select
    select-strategy="single"
    v-model="selection"
    return-object
    fixed-header
    :height="props.height"
    :hide-default-footer="props.hideFooter"
  >
    <template v-slot:no-data>
      <v-btn color="#6b4da9" @click="debouncedLoadItems()">{{ t('button.reload') }} </v-btn>
    </template>
    <template v-slot:item.isActive="{ item }">
      <v-checkbox-btn class="no-hover-cursor d-flex align-center" v-model="item.isActive" variant="outlined" density="compact" :readonly="true"></v-checkbox-btn>
    </template>
    <template #bottom v-if="!props.hideFooter">
      <div class="d-flex align-center flex-column flex-sm-row pa-3">
        <div class="d-flex align-center">
          <span class="text-caption me-2 mt-2">{{ $vuetify.locale.t('$vuetify.dataFooter.itemsPerPageText') }}</span>
          <v-select class="ultra-compact-select" v-model="itemsPerPage" :items="[25, 50, 100]" variant="plain" density="compact" hide-details></v-select>
        </div>
        <v-spacer></v-spacer>
        <v-pagination v-model="page" :length="pageCount" :total-visible="7" show-first-last-page variant="text" density="comfortable" size="small"></v-pagination>
        <v-spacer></v-spacer>
        <div class="flex-1-1-0"></div>
      </div>
    </template>
  </v-data-table>
</template>
<script setup>
import { ref, computed, defineModel, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import debounce from 'lodash.debounce';

/** @typedef {import('@/types/terminal-system').TerminalSystem} TerminalSystem */
/** @typedef {import('@/types/section').Section} Section */

const emit = defineEmits(['reload-items']);
const { t } = useI18n();

const props = defineProps({
  height: {
    type: String,
    default: 'calc(100vh - 290px)',
  },
  hideFooter: {
    type: Boolean,
    default: false,
  },
});

const model = defineModel({ type: Array, default: () => [] });
const sections = defineModel('sections', { type: Array, default: () => [] });
const selection = defineModel('selection', { type: Array, default: () => [] });
const system = defineModel('system', { type: Object, default: () => {} });
const loading = defineModel('loading', { type: Boolean, default: true });
const search = defineModel('search', { type: String, default: '' });
const page = defineModel('page', { type: Number, default: 1 });

const itemsPerPage = ref(25);
const itemsPerPageOptions = ref([
  { value: 25, title: '25' },
  { value: 50, title: '50' },
  { value: 100, title: '100' },
]);

const headers = ref([
  {
    title: t('model.section'),
    key: 'sectionName',
    align: /** @type {const} */ ('start'),
    sortable: true,
  },
  {
    title: t('model.description'),
    key: 'description',
    align: /** @type {const} */ ('start'),
    sortable: false,
  },
  {
    title: t('model.isActive'),
    key: 'isActive',
    align: /** @type {const} */ ('start'),
    sortable: false,
  },
  {
    title: t('model.order'),
    key: 'sort',
    align: /** @type {const} */ ('start'),
    sortable: false,
  },
]);

const pageCount = computed(() => {
  return Math.ceil(filteredItems.value.length / itemsPerPage.value) || 1;
});

const filteredItems = computed(() => {
  const q = search.value.trim().toLowerCase();
  if (!q) return model.value;
  return model.value.filter((item) => Object.values(item).some((v) => String(v).toLowerCase().includes(q)));
});

const debouncedLoadItems = debounce(() => {
  emit('reload-items', search.value);
}, 300);
</script>
